/**
 * Karte „Wirkung“ (Reisegruppen-Widget T5, Plan A5): Summen je Bildschirm-Schritt der letzten 30 Tage,
 * optional je Sprache. Quelle: `GET /api/widgets/:id/wirkung?tage=30` (angemeldet, RLS).
 *
 * Gemessen wird auf dem Server, ohne Cookies und Kennungen: nur Tageszähler je Schritt und Sprache,
 * keine Besucher, keine IP. „Abgeschickt“ und „nachgereicht“ zählt der Server selbst und sind damit
 * nicht fälschbar. Die Karte zeigt nur, was gemessen wurde: Anteile am Widget-Aufruf, keine Prognose
 * und kein Versprechen, was eine Änderung bringt.
 *
 * Fehlen Daten (Widget noch nie live, keine Aufrufe), steht „noch keine Daten“ statt einer Reihe Nullen.
 */
import { useState } from "react";
import { Card, ErrorNote, LoadingRows } from "../../ui/components";
import { useApi } from "../../hooks/useApi";
import { ABLAUF_SPRACHEN, ABLAUF_SPRACHE_LABEL, type AblaufSprache } from "./ablauf-modell";

/** Die Schritte in Reihenfolge des Gastwegs (Schlüssel wie im Server, `WIDGET_BILDSCHIRM_SCHRITTE`). */
export const WIRKUNG_SCHRITTE: { key: string; label: string }[] = [
  { key: "aufruf", label: "Widget aufgerufen" },
  { key: "start", label: "Schritt 1: Gruppengröße getippt" },
  { key: "zeitraum", label: "Schritt 2: Monat getippt" },
  { key: "kontakt", label: "Schritt 3: Name und E-Mail gesehen" },
  { key: "abgeschickt", label: "Anfrage abgeschickt" },
  { key: "dankeseite", label: "Dankeseite gesehen" },
  { key: "nachgereicht", label: "Zusatzangaben nachgereicht" },
];

type Summen = Record<string, number>;

export interface WirkungDaten {
  tage: number;
  schritte: Summen;
  sprachen: Record<string, Summen>;
}

function summen(v: unknown): Summen {
  const aus: Summen = {};
  if (typeof v !== "object" || v === null) return aus;
  for (const [k, n] of Object.entries(v as Record<string, unknown>)) if (typeof n === "number" && Number.isFinite(n)) aus[k] = n;
  return aus;
}

/** Fremdes JSON defensiv lesen; alles, was nicht die erwartete Form hat, gilt als „keine Daten“ (`null`). */
export function leseWirkung(roh: unknown): WirkungDaten | null {
  if (typeof roh !== "object" || roh === null) return null;
  const o = roh as Record<string, unknown>;
  if (typeof o.schritte !== "object" || o.schritte === null) return null;
  const sprachen: Record<string, Summen> = {};
  if (typeof o.sprachen === "object" && o.sprachen !== null) {
    for (const [l, s] of Object.entries(o.sprachen as Record<string, unknown>)) sprachen[l] = summen(s);
  }
  return { tage: typeof o.tage === "number" ? o.tage : 30, schritte: summen(o.schritte), sprachen };
}

const GESAMT = "alle";

export default function WirkungKarte({ widgetId }: { widgetId: string }) {
  const q = useApi<unknown>(`/api/widgets/${widgetId}/wirkung?tage=30`);
  const [sprache, setSprache] = useState<string>(GESAMT);

  const daten = leseWirkung(q.data);
  const gemessen = daten ? Object.values(daten.schritte).some((n) => n > 0) : false;
  const mitDaten = daten ? ABLAUF_SPRACHEN.filter((l) => Object.values(daten.sprachen[l] ?? {}).some((n) => n > 0)) : [];
  const zeile: Summen = daten ? (sprache === GESAMT ? daten.schritte : (daten.sprachen[sprache] ?? {})) : {};
  const aufrufe = zeile.aufruf ?? 0;

  return (
    <Card className="space-y-3 p-4" data-testid="wirkung-karte">
      <div>
        <p className="font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">Wirkung, letzte {daten?.tage ?? 30} Tage</p>
        <p className="mt-1 font-label-md text-label-md text-on-surface-variant">
          Wie weit kommen Besucher im Widget? MAESTRO zählt je Schritt und Sprache auf dem Server, ohne Cookies und Kennungen. Die Zahlen sagen, wo
          Besucher aufhören, nicht warum.
        </p>
      </div>

      {q.loading && !daten && <LoadingRows rows={3} />}
      {q.error && !daten && <ErrorNote title="Wirkung nicht ladbar" message={q.error} />}

      {!q.loading && !q.error && !gemessen && (
        <p className="rounded-xl bg-surface-container-lowest p-3 font-body-sm text-body-sm text-on-surface-variant dark:bg-white/[0.03]" data-testid="wirkung-leer">
          Noch keine Daten. Die Zählung beginnt, sobald das Widget live ist und auf Ihrer Website aufgerufen wird.
        </p>
      )}

      {gemessen && (
        <>
          {mitDaten.length > 1 && (
            <div role="group" aria-label="Sprache" className="flex flex-wrap gap-2">
              {[GESAMT, ...mitDaten].map((l) => (
                <button
                  key={l}
                  type="button"
                  aria-pressed={sprache === l}
                  className={`rounded-full border px-3 py-1 font-label-md text-label-md ${
                    sprache === l ? "border-primary bg-primary/10 font-semibold text-primary" : "border-outline-variant/40 text-on-surface-variant"
                  }`}
                  onClick={() => setSprache(l)}
                >
                  {l === GESAMT ? "Alle Sprachen" : ABLAUF_SPRACHE_LABEL[l as AblaufSprache]}
                </button>
              ))}
            </div>
          )}
          <table className="w-full font-body-sm text-body-sm">
            <caption className="sr-only">Summen je Schritt</caption>
            <thead>
              <tr className="text-left font-label-md text-label-md text-on-surface-variant">
                <th scope="col" className="py-1 pr-2 font-semibold">
                  Schritt
                </th>
                <th scope="col" className="py-1 pr-2 text-right font-semibold">
                  Anzahl
                </th>
                <th scope="col" className="py-1 text-right font-semibold">
                  vom Aufruf
                </th>
              </tr>
            </thead>
            <tbody>
              {WIRKUNG_SCHRITTE.map((s) => {
                const n = zeile[s.key] ?? 0;
                return (
                  <tr key={s.key} className="border-t border-outline-variant/20" data-testid={`wirkung-${s.key}`}>
                    <th scope="row" className="py-1.5 pr-2 text-left font-normal text-on-surface dark:text-inverse-on-surface">
                      {s.label}
                    </th>
                    <td className="py-1.5 pr-2 text-right font-semibold tabular-nums">{n}</td>
                    <td className="py-1.5 text-right tabular-nums text-on-surface-variant">
                      {s.key === "aufruf" || aufrufe === 0 ? "–" : `${Math.round((n / aufrufe) * 100)} %`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="font-label-md text-label-md text-on-surface-variant">
            „Anfrage abgeschickt“ und „Zusatzangaben nachgereicht“ zählt der Server selbst, sie sind nicht fälschbar. Die übrigen Schritte meldet der
            Browser des Besuchers, je Seitenaufruf einmal.
          </p>
        </>
      )}
    </Card>
  );
}
